/*
 * steam-hero.js
 * 湯気の粒子が見出しの文字を形づくり、その後もきらめき続けるヒーロー。
 *
 * 3つのフェーズで構成する。
 *   forming  : 粒子が二次ベジェ曲線を描いて文字の形へ収束する
 *   revealing: 粒子をフェードアウトさせ、実テキスト(DOM)へ引き継ぐ
 *   idle     : 文字の粒子は破棄し、きらめきと薄い湯気だけを描き続ける
 *
 * 負荷対策
 *   - グローは shadowBlur を使わず、起動時に焼いたスプライトを drawImage する
 *     （shadowBlur は1粒ごとにぼかしを再計算するため桁違いに重い）
 *   - idle では粒子数が1/6以下に落ちるので、常時描画でも負荷が小さい
 *   - devicePixelRatio は 1.5 で頭打ち、粒子数は面積とCPUコア数から自動調整
 *   - 画面外・非アクティブタブでは requestAnimationFrame を止める
 *   - prefers-reduced-motion では canvas を一切動かさない
 */

(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MAX_DPR = 1.5;
  const REVEAL_MS = 900;

  /* 始点・終点の速度が0になるイージング。到達時に不自然に止まらない */
  const smootherstep = (t) => t * t * t * (t * (t * 6 - 15) + 10);

  /* 背景が暗ければ加算合成、明るければ通常合成にする */
  const isDark = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    if (!m) return true;
    const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16) / 255);
    const lin = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) < 0.35;
  };

  /* 放射グラデーションのスプライトを1枚だけ焼く */
  const makeSprite = (color, size, coreStop) => {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const x = c.getContext('2d');
    const g = x.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, color);
    g.addColorStop(coreStop, color);
    g.addColorStop(1, 'transparent');
    x.fillStyle = g;
    x.globalAlpha = 1;
    x.beginPath();
    x.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    x.fill();
    return c;
  };

  /*
   * きらめき用スプライト。
   * 十字のフレアや硬い輝点は安っぽく見えるため使わない。
   * ごく小さな芯のまわりに、裾を長く引いたにじみだけを置く。
   */
  const makeSparkSprite = (color, size) => {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const x = c.getContext('2d');
    const h = size / 2;

    const g = x.createRadialGradient(h, h, 0, h, h, h);
    g.addColorStop(0, color);
    g.addColorStop(0.06, color);
    g.addColorStop(0.22, 'rgb(255 255 255 / 0%)');
    g.addColorStop(1, 'transparent');

    // 芯（ごく小さい）
    x.globalAlpha = 0.55;
    x.fillStyle = g;
    x.beginPath();
    x.arc(h, h, h, 0, Math.PI * 2);
    x.fill();

    // 裾の広いにじみを重ねて、輪郭を持たない光にする
    const soft = x.createRadialGradient(h, h, 0, h, h, h);
    soft.addColorStop(0, color);
    soft.addColorStop(1, 'transparent');
    x.globalAlpha = 0.22;
    x.fillStyle = soft;
    x.beginPath();
    x.arc(h, h, h, 0, Math.PI * 2);
    x.fill();
    return c;
  };

  class SteamHero {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('.steam-hero__canvas');
      this.headline = root.querySelector('.steam-hero__headline');
      this.replayBtn = root.querySelector('.steam-hero__replay');
      if (!this.canvas || !this.headline) return;

      this.ctx = this.canvas.getContext('2d', { alpha: true });
      if (!this.ctx) return;

      const d = root.dataset;
      this.vaporColor = d.particleColor || '#eaf3eb';
      this.sparkColor = d.sparkleColor || '#e4cca2';
      this.duration = (parseFloat(d.duration) || 3.4) * 1000;
      this.requested = parseInt(d.particleCount, 10) || 700;
      // 0 を指定してきらめきを止められるよう、|| でのフォールバックは使わない
      const density = parseInt(d.sparkleDensity, 10);
      this.sparkDensity = Number.isFinite(density) ? density : 18;
      this.interactive = d.interactive !== 'false';
      this.blend = isDark(d.bgColor) ? 'lighter' : 'source-over';

      this.phase = 'idle';
      this.particles = [];
      this.sparks = [];
      this.vapor = [];
      this.pointer = { x: -9999, y: -9999, active: false };
      this.raf = null;
      this.visible = true;

      this.onResize = this.debounce(() => this.setup(), 220);
      window.addEventListener('resize', this.onResize, { passive: true });

      document.addEventListener('visibilitychange', () => {
        this.visible = !document.hidden;
        this.visible ? this.resume() : this.stop();
      });

      if (this.replayBtn) this.replayBtn.addEventListener('click', () => this.play());

      if (this.interactive) {
        root.addEventListener('pointermove', (e) => this.onPointer(e), { passive: true });
        root.addEventListener('pointerleave', () => {
          this.pointer.active = false;
        });
        root.addEventListener('click', (e) => {
          if (e.target.closest('.steam-hero__replay')) return;
          this.play();
        });
      }

      REDUCED.addEventListener('change', () => this.applyMotionPreference());
      this.applyMotionPreference();
    }

    applyMotionPreference() {
      if (REDUCED.matches) {
        this.stop();
        this.root.classList.remove('steam-hero--animating', 'is-revealed');
        return;
      }
      this.setup();
      this.observe();
    }

    debounce(fn, wait) {
      let t;
      return (...a) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...a), wait);
      };
    }

    onPointer(e) {
      const r = this.root.getBoundingClientRect();
      this.pointer.x = e.clientX - r.left;
      this.pointer.y = e.clientY - r.top;
      this.pointer.active = true;
    }

    setup() {
      if (REDUCED.matches) return;

      const r = this.root.getBoundingClientRect();
      const w = Math.round(r.width);
      const h = Math.round(r.height);
      if (!w || !h) return;

      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.w = w;
      this.h = h;

      // 面積とCPUコア数から上限を決める。設定値を超えることはない
      const byArea = Math.round((w * h) / 2400);
      const cores = navigator.hardwareConcurrency || 4;
      const budget = cores <= 4 ? 0.6 : 1;
      this.count = Math.max(220, Math.min(this.requested, Math.round(byArea * budget)));

      this.vaporSprite = makeSprite(this.vaporColor, 48, 0.08);
      this.sparkSprite = makeSparkSprite(this.sparkColor, 34);

      this.targets = this.sampleHeadline(w, h);
      this.seedVapor();
      this.seedSparks();

      if (this.targets.length && !this.hasPlayed) {
        this.play();
      } else {
        this.phase = 'idle';
        this.resume();
      }
    }

    /* 実テキストと同じフォント・位置で描き、不透明画素を収束先として拾う */
    sampleHeadline(w, h) {
      const off = document.createElement('canvas');
      off.width = w;
      off.height = h;
      const o = off.getContext('2d', { willReadFrequently: true });
      if (!o) return [];

      const cs = getComputedStyle(this.headline);
      const hr = this.headline.getBoundingClientRect();
      const rr = this.root.getBoundingClientRect();

      o.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} / ${cs.lineHeight} ${cs.fontFamily}`;
      o.textAlign = 'center';
      o.textBaseline = 'middle';
      if ('letterSpacing' in o) o.letterSpacing = cs.letterSpacing;
      o.fillStyle = '#fff';
      o.fillText(
        this.headline.textContent.trim(),
        hr.left - rr.left + hr.width / 2,
        hr.top - rr.top + hr.height / 2
      );

      const data = o.getImageData(0, 0, w, h).data;
      const pts = [];
      let step = Math.max(2, Math.round(Math.sqrt((w * h) / (this.count * 14))));

      for (let attempt = 0; attempt < 8; attempt++) {
        pts.length = 0;
        for (let y = 0; y < h; y += step) {
          for (let x = 0; x < w; x += step) {
            if (data[(y * w + x) * 4 + 3] > 128) pts.push({ x, y });
          }
        }
        if (pts.length <= this.count) break;
        step += 1;
      }
      return pts;
    }

    /* idle 中ずっと漂う薄い湯気。数を絞って常時描画しても軽くする */
    seedVapor() {
      const n = Math.max(28, Math.round(this.count * 0.11));
      this.vapor = Array.from({ length: n }, () => ({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        r: 14 + Math.random() * 30,
        a: 0.03 + Math.random() * 0.05,
        vy: -(4 + Math.random() * 10) / 1000,
        drift: (Math.random() - 0.5) * 0.012,
        ph: Math.random() * Math.PI * 2,
        ox: 0,
        oy: 0,
      }));
    }

    seedSparks() {
      this.sparks = Array.from({ length: this.sparkDensity }, () => this.newSpark(true));
    }

    /*
     * きらめきは文字の輪郭上からランダムに湧かせ、文字自体が光って見えるようにする。
     * 粒は小さく、寿命を長めに取ってゆっくり明滅させる。
     */
    newSpark(initial) {
      const t =
        this.targets && this.targets.length
          ? this.targets[(Math.random() * this.targets.length) | 0]
          : { x: Math.random() * this.w, y: Math.random() * this.h };
      return {
        x: t.x + (Math.random() - 0.5) * 14,
        y: t.y + (Math.random() - 0.5) * 14,
        size: 2.5 + Math.random() * 5.5,
        born: performance.now() - (initial ? Math.random() * 2600 : 0),
        life: 1600 + Math.random() * 2600,
        rise: (Math.random() * 5 + 2) / 1000,
      };
    }

    play() {
      if (!this.targets || !this.targets.length || REDUCED.matches) return;
      this.hasPlayed = true;
      this.root.classList.add('steam-hero--animating');
      this.root.classList.remove('is-revealed');

      const pts = this.targets.slice();
      for (let i = pts.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        [pts[i], pts[j]] = [pts[j], pts[i]];
      }

      this.particles = pts.map((t) => {
        const x0 = this.w * (0.12 + Math.random() * 0.76);
        const y0 = this.h + 30 + Math.random() * this.h * 0.5;
        // 制御点を進行方向の左右にずらし、立ち上る湯気らしい曲線を描かせる
        const mx = (x0 + t.x) / 2 + (Math.random() - 0.5) * this.w * 0.34;
        const my = (y0 + t.y) / 2 - this.h * (0.12 + Math.random() * 0.2);
        // 文字の下側・左側から順に形になるよう時間差をつける
        const nx = t.x / this.w;
        const ny = t.y / this.h;
        return {
          x0, y0, cx: mx, cy: my, tx: t.x, ty: t.y,
          delay: (nx * 0.55 + (1 - ny) * 0.45) * 0.42 + Math.random() * 0.1,
          size: 4 + Math.random() * 7,
          ph: Math.random() * Math.PI * 2,
        };
      });

      this.phase = 'forming';
      this.start = performance.now();
      this.resume();
    }

    resume() {
      if (this.raf || REDUCED.matches || !this.visible || this.offscreen) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame((t) => this.loop(t));
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    loop(now) {
      this.raf = requestAnimationFrame((t) => this.loop(t));
      const dt = Math.min(now - (this.last || now), 50);
      this.last = now;

      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.w, this.h);
      ctx.globalCompositeOperation = this.blend;

      if (this.phase === 'forming' || this.phase === 'revealing') {
        this.drawForming(now, ctx);
      }
      this.drawVapor(now, dt, ctx);
      this.drawSparks(now, ctx);

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }

    drawForming(now, ctx) {
      const elapsed = now - this.start;
      const t = Math.min(elapsed / this.duration, 1);

      // 収束後は粒子を徐々に消し、実テキストへ引き継ぐ
      let fade = 1;
      if (this.phase === 'revealing') {
        fade = 1 - Math.min((elapsed - this.duration) / REVEAL_MS, 1);
      }

      const sprite = this.vaporSprite;
      for (const p of this.particles) {
        const lp = Math.max(0, Math.min((t - p.delay) / (1 - p.delay), 1));
        const e = smootherstep(lp);
        const mt = 1 - e;

        const x = mt * mt * p.x0 + 2 * mt * e * p.cx + e * e * p.tx;
        const y = mt * mt * p.y0 + 2 * mt * e * p.cy + e * e * p.ty;

        // 収束するほど小さく締まり、輪郭が立つ
        const s = p.size * (2.1 - e * 1.25);
        const breathe = 1 + Math.sin(p.ph + now / 900) * 0.06 * (1 - e);
        ctx.globalAlpha = Math.min(1, (0.12 + e * 0.72) * fade);
        ctx.drawImage(sprite, x - (s * breathe) / 2, y - (s * breathe) / 2, s * breathe, s * breathe);
      }

      if (this.phase === 'forming' && t >= 1) {
        this.phase = 'revealing';
        this.root.classList.add('is-revealed');
      } else if (this.phase === 'revealing' && fade <= 0) {
        this.phase = 'idle';
        this.particles = [];
      }
    }

    drawVapor(now, dt, ctx) {
      const sprite = this.vaporSprite;
      const pr = this.pointer;

      for (const v of this.vapor) {
        v.y += v.vy * dt * 16;
        v.x += Math.sin(v.ph + now / 2600) * v.drift * dt;

        if (v.y < -v.r) {
          v.y = this.h + v.r;
          v.x = Math.random() * this.w;
        }

        // ポインタ付近をやわらかく押しのける。戻りはバネ状に減衰
        if (pr.active) {
          const dx = v.x - pr.x;
          const dy = v.y - pr.y;
          const d2 = dx * dx + dy * dy;
          const R = 150;
          if (d2 < R * R && d2 > 0.01) {
            const f = (1 - Math.sqrt(d2) / R) * 26;
            const inv = 1 / Math.sqrt(d2);
            v.ox += dx * inv * f * 0.06;
            v.oy += dy * inv * f * 0.06;
          }
        }
        v.ox *= 0.92;
        v.oy *= 0.92;

        const s = v.r * 2;
        ctx.globalAlpha = v.a;
        ctx.drawImage(sprite, v.x + v.ox - v.r, v.y + v.oy - v.r, s, s);
      }
    }

    drawSparks(now, ctx) {
      const sprite = this.sparkSprite;

      for (let i = 0; i < this.sparks.length; i++) {
        const s = this.sparks[i];
        const age = now - s.born;
        if (age > s.life) {
          this.sparks[i] = this.newSpark(false);
          continue;
        }
        const k = age / s.life;
        // 山なりにゆっくり明滅させる。二乗して裾を寝かせ、点滅感を消す
        const alpha = Math.pow(Math.sin(k * Math.PI), 2);
        const size = s.size * (0.8 + alpha * 0.35);
        const y = s.y - age * s.rise;

        ctx.globalAlpha = alpha * 0.5;
        ctx.drawImage(sprite, s.x - size / 2, y - size / 2, size, size);
      }

      // ポインタを動かすと、その周辺にも光が湧く
      if (this.sparks.length && this.pointer.active && this.interactive && Math.random() < 0.1) {
        const s = this.newSpark(false);
        s.x = this.pointer.x + (Math.random() - 0.5) * 90;
        s.y = this.pointer.y + (Math.random() - 0.5) * 90;
        this.sparks[(Math.random() * this.sparks.length) | 0] = s;
      }
    }

    observe() {
      if (this.observer) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            this.offscreen = !e.isIntersecting;
            this.offscreen ? this.stop() : this.resume();
          }
        },
        { threshold: 0 }
      );
      this.observer.observe(this.root);
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('.steam-hero').forEach((el) => {
      if (el.dataset.shInit) return;
      el.dataset.shInit = '1';
      // 字形が確定してから計測しないと収束先がずれる
      const start = () => new SteamHero(el);
      document.fonts && document.fonts.ready ? document.fonts.ready.then(start) : start();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
